import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Alert, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

export const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

const debounce = (fn, wait) => {
    let t;
    const d = (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
    };
    d.flush = (...args) => {
        clearTimeout(t);
        fn(...args);
    };
    d.cancel = () => clearTimeout(t);
    return d;
};

const AdditionalNotes = ({
                             index,
                             id,
                             description,
                             onChange,
                             isEditMode,
                             isFormArchived,
                             placeholderValues,
                             readOnly,
                             accessLevel,
                             audience,
                             recipientType,
                             submitMode,
                         }) => {
    const [currentValue, setCurrentValue] = useState(description ?? '');

    const isFocusedRef = useRef(false);
    const currentValueRef = useRef(currentValue);
    currentValueRef.current = currentValue;

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const commitUpstream = useCallback((nextValue) => {
        if (!onChangeRef.current) return;

        onChangeRef.current({
            index,
            description: stripHtmlTags(nextValue),
            formattedDescription: nextValue,
        });
    }, [index]);

    const debouncedCommit = useMemo(
        () => debounce(commitUpstream, 200),
        [commitUpstream]
    );

    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    useEffect(() => {
        if (isFocusedRef.current) return;

        const next = description ?? '';
        if (next !== currentValueRef.current) {
            setCurrentValue(next);
        }
    }, [description]);

    const handleDraftChange = useCallback((html) => {
        setCurrentValue(html);
        debouncedCommit(html);
    }, [debouncedCommit]);

    const handleFocus = useCallback(() => {
        isFocusedRef.current = true;
    }, []);

    const handleBlur = useCallback(() => {
        isFocusedRef.current = false;
        debouncedCommit.flush(currentValueRef.current);
    }, [debouncedCommit]);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <Box sx={{
                        maxWidth: '52vw',
                        cursor: 'pointer'
                    }}>
                        <RichEditor
                            onChange={handleDraftChange}
                            id={id}
                            index={index}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={currentValue}
                            setCurrentDescription={handleDraftChange}
                            audience={audience}
                            recipientType={recipientType}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                    {currentValue && currentValue.length > 200000 && (
                        <Alert severity="warning" sx={{mt: 2}}>
                            <Typography variant="body2">
                                <strong>Warning:</strong> The description is longer than 200,000 characters (
                                {currentValue.length.toLocaleString()} characters). Such long descriptions will not be
                                included in emails - the default description will be used instead. Please reduce the
                                content or use smaller images to ensure the description is included in email
                                notifications.
                            </Typography>
                        </Alert>
                    )}
                </Box>
                )
                : (
                    <RichEditorResultText
                        id={id}
                        description={description}
                        placeholderValues={placeholderValues}
                        readonly={readOnly}
                        enableValidation={false}
                        audience={audience}
                        recipientType={recipientType}
                        submitMode={submitMode}
                    />
                )}
        </Box>
    );
};

AdditionalNotes.propTypes = {
    id: PropTypes.string,
    description: PropTypes.string,
    index: PropTypes.number,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    submitMode: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
};

export default AdditionalNotes;
